import { store } from "../state";
import {
  DEFAULT_HOUR,
  disableReminders,
  enableReminders,
  hourLabel,
  isIOS,
  isStandalone,
  notificationReadiness,
  pushServerKey,
  sendTestNotification,
  updateBadge,
  type TestResult,
} from "../notify";
import { mountLookPicker } from "../look";
import { JUMP_KEY } from "./reminderBell";

const BACK_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>`;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function toLocalInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const SHARE_SVG = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 15V3M8 7l4-4 4 4"/><path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1"/></svg>`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

export function renderSettings(root: HTMLElement): () => void {
  let currentCleanup: (() => void) | null = null;

  function draw() {
    const state = store.get();
    const event = store.getPrimaryEvent();
    const notif = state.notifications;

    // Read the live permission rather than trusting what we stored: it can be
    // changed in iOS Settings while the app is closed.
    const readiness = notificationReadiness();
    const notifOn = notif.enabled && readiness === "granted";
    const pushLive = notifOn && !!notif.push;
    const hour = notif.hour ?? DEFAULT_HOUR;
    const canBadge = "setAppBadge" in navigator;
    const permissionLabel =
      readiness === "install"
        ? "Reminders need the Home Screen app. Add it using the steps at the top, then open it from there."
        : readiness === "unsupported"
          ? "This browser can't show notifications."
          : readiness === "denied"
            ? "Blocked. Turn them on for C*UNTDOWN in your phone's Settings, under Notifications."
            : pushLive
              ? `On. One a day at ${hourLabel(hour).toLowerCase()}, even with the app closed.`
              : notifOn
                ? "On, but only when you open the app: the reminder server isn't set up yet."
                : "Off.";
    const showInstall = isIOS() && !isStandalone();

    const defaultTarget = new Date();
    defaultTarget.setDate(defaultTarget.getDate() + 30);
    defaultTarget.setHours(9, 0, 0, 0);
    const startValue = event ? toLocalInputValue(new Date(event.startDate)) : toLocalInputValue(new Date());
    const endValue = event ? toLocalInputValue(new Date(event.targetDate)) : toLocalInputValue(defaultTarget);
    const nowLocal = toLocalInputValue(new Date());

    root.innerHTML = `
      <div class="subscreen subscreen--dense">
        <div class="subscreen-header">
          <a class="back-btn" href="#/" aria-label="Back">${BACK_SVG}</a>
          <div class="subscreen-title">Settings</div>
        </div>

        ${
          showInstall
            ? `<div class="section install-card">
          <div class="section-title">Put it on your Home Screen</div>
          <ol class="install-steps">
            <li>Tap <span class="install-share">${SHARE_SVG} Share</span> in Safari's toolbar.</li>
            <li>Choose <b>Add to Home Screen</b>, then <b>Add</b>.</li>
            <li>Open C*UNTDOWN from the Home Screen and set it up there. That's the version that can send reminders.</li>
          </ol>
        </div>`
            : ""
        }

        <div class="section">
          <div class="section-title">Appearance</div>
          <div data-el="lookPicker"></div>
        </div>

        <div class="section">
          <div class="section-title">Your countdown</div>
          <form class="field-group" data-el="eventForm">
            <div class="field-row">
              <div class="field">
                <label for="s-name">Event name</label>
                <input id="s-name" name="name" type="text" placeholder="Stag Do" maxlength="60" autocomplete="off" value="${escapeHtml(event?.name ?? "")}" required />
              </div>
              <div class="field">
                <label for="s-person">Person's name</label>
                <input id="s-person" name="personName" type="text" placeholder="Dan" maxlength="60" autocomplete="off" value="${escapeHtml(event?.personName ?? "")}" />
              </div>
            </div>
            <div class="field-row">
              <div class="field">
                <label for="s-start">Start date</label>
                <input id="s-start" name="startDate" type="datetime-local" value="${startValue}" required />
              </div>
              <div class="field">
                <label for="s-date">End date</label>
                <input id="s-date" name="targetDate" type="datetime-local" min="${nowLocal}" value="${endValue}" required />
              </div>
            </div>
            <button class="btn btn-primary" type="submit" data-el="saveBtn">${event ? "Save changes" : "Start the countdown"}</button>
          </form>
        </div>

        <div class="section">
          <div class="section-title">Preferences</div>
          <div class="row" style="border-top:none;padding-top:0">
            <div class="row-label">Censor swearing</div>
            <label class="toggle"><input type="checkbox" switch data-el="censorSwitch" ${state.censorEnabled ? "checked" : ""} aria-label="Censor swearing" /><span class="switch" aria-hidden="true"></span></label>
          </div>
        </div>

        <div class="section" data-el="remindersSection">
          <div class="section-title">Daily reminders</div>
          <div class="row" style="border-top:none;padding-top:0">
            <div class="row-label">Notify me daily</div>
            <label class="toggle"><input type="checkbox" switch data-el="notifSwitch" ${notifOn ? "checked" : ""} aria-label="Notify me daily" /><span class="switch" aria-hidden="true"></span></label>
          </div>
          <div class="row-value" data-el="permLabel">${escapeHtml(permissionLabel)}</div>
          ${
            pushLive
              ? `<div class="row">
            <label class="row-label" for="s-hour">Reminder time</label>
            <select id="s-hour" class="row-select" data-el="hourSelect">
              ${Array.from({ length: 24 }, (_, h) => `<option value="${h}"${h === hour ? " selected" : ""}>${hourLabel(h)}</option>`).join("")}
            </select>
          </div>`
              : ""
          }
          ${
            notifOn && canBadge
              ? `<div class="row">
            <div class="row-label">Days left on the icon</div>
            <label class="toggle"><input type="checkbox" switch data-el="badgeSwitch" ${notif.badge !== false ? "checked" : ""} aria-label="Days left on the icon" /><span class="switch" aria-hidden="true"></span></label>
          </div>`
              : ""
          }
          <div class="notif-tests">
            <button type="button" class="btn btn-ghost notif-test" data-el="testBtn">${pushLive ? "Send a test" : "Send a test notification"}</button>
            ${pushLive ? `<button type="button" class="btn btn-ghost notif-test" data-el="testLaterBtn">Test in 10s</button>` : ""}
          </div>
          <div class="row-value notif-test-result" data-el="testResult" role="status" aria-live="polite"></div>
        </div>

        <div class="section">
          <button type="button" class="btn btn-danger" data-el="resetBtn">Erase everything on this device</button>
        </div>
      </div>
    `;

    const unmountLook = mountLookPicker(root.querySelector<HTMLElement>('[data-el="lookPicker"]')!);
    const eventForm = root.querySelector<HTMLFormElement>('[data-el="eventForm"]')!;
    const nameInput = root.querySelector<HTMLInputElement>("#s-name")!;
    const startInput = root.querySelector<HTMLInputElement>("#s-start")!;
    const dateInput = root.querySelector<HTMLInputElement>("#s-date")!;
    const saveBtn = root.querySelector<HTMLButtonElement>('[data-el="saveBtn"]')!;
    const censorSwitch = root.querySelector<HTMLInputElement>('[data-el="censorSwitch"]')!;
    const notifSwitch = root.querySelector<HTMLInputElement>('[data-el="notifSwitch"]')!;
    const permLabel = root.querySelector<HTMLElement>('[data-el="permLabel"]')!;
    const resetBtn = root.querySelector<HTMLButtonElement>('[data-el="resetBtn"]')!;
    const testBtn = root.querySelector<HTMLButtonElement>('[data-el="testBtn"]')!;
    const testLaterBtn = root.querySelector<HTMLButtonElement>('[data-el="testLaterBtn"]');
    const hourSelect = root.querySelector<HTMLSelectElement>('[data-el="hourSelect"]');
    const badgeSwitch = root.querySelector<HTMLInputElement>('[data-el="badgeSwitch"]');
    const testResult = root.querySelector<HTMLElement>('[data-el="testResult"]')!;

    function validateEventForm() {
      const hasName = nameInput.value.trim().length > 0;
      const start = startInput.value ? new Date(startInput.value).getTime() : NaN;
      const end = dateInput.value ? new Date(dateInput.value).getTime() : NaN;
      const hasDates = Boolean(start) && Boolean(end) && end > Date.now() && end > start;
      saveBtn.disabled = !(hasName && hasDates);
    }
    eventForm.addEventListener("input", validateEventForm);

    function onSubmitEvent(e: Event) {
      e.preventDefault();
      const name = nameInput.value.trim();
      const personName = root.querySelector<HTMLInputElement>("#s-person")!.value.trim();
      const start = new Date(startInput.value).getTime();
      const end = new Date(dateInput.value).getTime();
      if (!name || !end || end <= Date.now() || end <= start) return;
      store.saveEvent(
        { name, personName, targetDate: new Date(end).toISOString(), startDate: new Date(start).toISOString() },
        event?.id
      );
      draw();
    }
    eventForm.addEventListener("submit", onSubmitEvent);

    function onToggleCensor() {
      store.setCensorEnabled(!store.get().censorEnabled);
      draw();
    }
    censorSwitch.addEventListener("change", onToggleCensor);

    // Fetch the push key now, so turning reminders on doesn't wait on the
    // network between the tap and the permission prompt.
    void pushServerKey();

    async function onToggleNotif() {
      if (store.get().notifications.enabled && notificationReadiness() === "granted") {
        await disableReminders();
      } else {
        permLabel.textContent = "Asking…";
        const result = await enableReminders();
        if (result === "install" || result === "unsupported") store.setNotificationPrefs({ enabled: false, lastPermission: "unsupported" });
      }
      if (notifSwitch.isConnected) draw();
    }
    notifSwitch.addEventListener("change", onToggleNotif);

    function onHourChange() {
      store.setNotificationPrefs({ hour: Number(hourSelect!.value) });
      draw();
    }
    hourSelect?.addEventListener("change", onHourChange);

    function onToggleBadge() {
      store.setNotificationPrefs({ badge: store.get().notifications.badge === false });
      void updateBadge();
      draw();
    }
    badgeSwitch?.addEventListener("change", onToggleBadge);

    const TEST_MESSAGES: Record<TestResult, string> = {
      sent: pushLive
        ? "Sent from the server, the same way the daily one comes. It should land in a second or two."
        : "Sent. It shows as a banner, or in Notification Centre if the app is open. Swipe down from the top to check.",
      busy: "One's already on its way. Give it a few seconds.",
      scheduled: "Coming in 10 seconds. Lock your phone now to see it arrive like the real thing.",
      denied: "Notifications are blocked. Turn them on for C*UNTDOWN in your phone's Settings, under Notifications.",
      install: "On iPhone, add C*UNTDOWN to your Home Screen first, then open it from there and try again.",
      unsupported: "This browser can't show notifications.",
      failed: "The phone didn't accept it. Close the app fully, reopen it from the Home Screen and try again.",
      "server-failed": "The server couldn't reach your phone. Turn reminders off and on again, then retry.",
    };
    async function runTest(delay: number) {
      testBtn.disabled = true;
      if (testLaterBtn) testLaterBtn.disabled = true;
      testResult.textContent = "Sending…";
      delete testResult.dataset.tone;
      const { result, detail } = await sendTestNotification(delay);
      if (!testBtn.isConnected) return;
      testBtn.disabled = false;
      if (testLaterBtn) testLaterBtn.disabled = false;
      testResult.textContent = detail ? `${TEST_MESSAGES[result]} ${detail}` : TEST_MESSAGES[result];
      testResult.dataset.tone = result === "sent" || result === "scheduled" ? "good" : "bad";
    }
    const onTest = () => runTest(0);
    const onTestLater = () => runTest(10);
    testBtn.addEventListener("click", onTest);
    testLaterBtn?.addEventListener("click", onTestLater);

    function onReset() {
      if (confirm("This deletes your countdown and preferences from this device. Can't be undone. Sure?")) {
        store.resetAll();
        location.hash = "#/";
      }
    }
    resetBtn.addEventListener("click", onReset);

    currentCleanup = () => {
      unmountLook();
      eventForm.removeEventListener("input", validateEventForm);
      eventForm.removeEventListener("submit", onSubmitEvent);
      censorSwitch.removeEventListener("change", onToggleCensor);
      notifSwitch.removeEventListener("change", onToggleNotif);
      testBtn.removeEventListener("click", onTest);
      testLaterBtn?.removeEventListener("click", onTestLater);
      hourSelect?.removeEventListener("change", onHourChange);
      badgeSwitch?.removeEventListener("change", onToggleBadge);
      resetBtn.removeEventListener("click", onReset);
    };
  }

  draw();

  // Arrived from the bell on the countdown: start at the reminders.
  let jump: string | null = null;
  try {
    jump = sessionStorage.getItem(JUMP_KEY);
    sessionStorage.removeItem(JUMP_KEY);
  } catch {
    // private mode
  }
  if (jump === "reminders") {
    const section = root.querySelector<HTMLElement>('[data-el="remindersSection"]');
    if (section) {
      section.scrollIntoView({ block: "start" });
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) section.classList.add("is-flagged");
    }
  }

  return () => {
    currentCleanup?.();
  };
}
